import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import ResumePaywallModal from '../components/ResumePaywallModal';
import FeatureUsageMeter from '../components/FeatureUsageMeter';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus,
  Search,
  Filter,
  FileText,
  Trash2,
  RefreshCw,
  Calendar,
  Target,
  TrendingUp,
  AlertCircle,
  Copy,
  Sparkles,
  LayoutGrid,
  PenLine,
  Linkedin,
  MessageSquare,
  Gift,
  Clock,
  ArrowRight,
} from 'lucide-react';

import { logger } from '../utils/logger';
import { analytics } from '../utils/analytics';
import { API } from '../utils/apiBase';
import ManageSubscriptionBanner from '../components/ManageSubscriptionBanner';
import ResumeStudio from './ResumeStudio';

// Shared motion variants
const cardFadeIn = { opacity: 0, y: 20 };
const cardFadeInAnimate = { opacity: 1, y: 0 };
const bannerSlideIn = { opacity: 0, y: -20 };
const bannerSlideInAnimate = { opacity: 1, y: 0 };


// Helper function to get score color
const getScoreColor = (score) => {
  if (score < 50) return { text: 'text-[#e11d48]', bg: 'bg-[#e11d48]', border: 'border-t-[#e11d48]' };
  if (score <= 75) return { text: 'text-[#eab308]', bg: 'bg-[#eab308]', border: 'border-t-[#eab308]' };
  return { text: 'text-[#10b981]', bg: 'bg-[#10b981]', border: 'border-t-[#10b981]' };
};

/**
 * CompTrialBanner — countdown + lock-in CTA shown to users on a comp Pro grant.
 * Reads `pro_expires_at` from /auth/me. Renders nothing for paying users or
 * users without an active comp grant. Pre-applies the WINBACK50 promo so the
 * upgrade is one tap, and the discount frame ("$4.50/mo") makes lock-in feel
 * like upside vs. losing free Pro.
 */
const CompTrialBanner = ({ expiresAt }) => {
  if (!expiresAt) return null;
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return null;
  const msLeft = expiry.getTime() - Date.now();
  if (msLeft <= 0) return null;
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  const hoursLeft = Math.ceil(msLeft / (1000 * 60 * 60));
  // < 24h gets an urgent red treatment; otherwise blue/calm.
  const urgent = daysLeft <= 1;
  const label = urgent
    ? (hoursLeft <= 1 ? 'less than an hour' : `${hoursLeft} hours`)
    : `${daysLeft} day${daysLeft === 1 ? '' : 's'}`;
  return (
    <motion.div
      initial={bannerSlideIn}
      animate={bannerSlideInAnimate}
      className={`mb-6 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-white ${urgent ? 'bg-gradient-to-r from-[#7f1d1d] to-[#991b1b]' : 'bg-gradient-to-r from-[#0f1115] via-[#001d6a] to-[#002fa7]'}`}
      data-testid="comp-trial-banner"
    >
      <div className="flex items-start sm:items-center gap-3">
        <Clock size={20} className="flex-shrink-0 mt-0.5 sm:mt-0" />
        <div>
          <p className="font-bold text-sm sm:text-base">
            {urgent
              ? `Your complimentary Pro access ends in ${label}.`
              : `${label} left of complimentary Pro access.`}
          </p>
          <p className="text-xs sm:text-sm text-white/80 mt-0.5">
            Lock it in for <strong>$4.50/mo</strong> &mdash; 50% off your first month with code WINBACK50, no commitment.
          </p>
        </div>
      </div>
      <Link
        to="/pricing?promo=WINBACK50&utm_source=trial_banner&utm_medium=in_app"
        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white text-[#0f1115] font-bold text-sm hover:bg-white/90 transition-colors whitespace-nowrap"
        data-testid="trial-banner-cta"
      >
        Lock in $4.50/mo <ArrowRight size={14} />
      </Link>
    </motion.div>
  );
};

// Stats Card Component
const StatsCard = ({ label, value, icon: Icon, iconColor }) => (
  <div className="dashboard-card p-6">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6b7280]">
        {label}
      </span>
      <Icon size={20} className={iconColor} />
    </div>
    <div className="text-3xl font-satoshi font-black text-[#0f1115]">
      {value}
    </div>
  </div>
);

const cardTransitionBase = { duration: 0.3 };

// Resume Card Component
const ResumeCard = ({ resume, onReoptimize, onDelete, onDuplicate, onOpenStudio, index }) => {
  const colors = getScoreColor(resume.ats_score);
  const cardTransition = { ...cardTransitionBase, delay: index * 0.05 };
  
  return (
    <motion.div
      initial={cardFadeIn}
      animate={cardFadeInAnimate}
      transition={cardTransition}
      className={`dashboard-card border-t-4 ${colors.border}`}
      data-testid={`resume-card-${resume.resume_id}`}
    >
      <Link to={`/resume/${resume.resume_id}`} className="block p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-satoshi text-lg font-bold text-[#0f1115] truncate">
              {resume.title}
            </h3>
            <p className="text-sm text-[#6b7280] truncate">{resume.target_job}</p>
          </div>
          <div className={`text-3xl font-satoshi font-black ${colors.text}`}>
            {resume.ats_score}
          </div>
        </div>
        <div className="mb-4">
          <div className="h-2 bg-[#e5e7eb] w-full">
            <div
              className={`h-full ${colors.bg}`}
              style={{ width: `${resume.ats_score}%` }}
            ></div>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-[#6b7280]">
          <span>
            {resume.keywords_matched?.length || 0} keywords matched
          </span>
          <span>
            {new Date(resume.created_at).toLocaleDateString()}
          </span>
        </div>
      </Link>
      <div className="flex border-t border-[#e5e7eb]">
        <button
          onClick={() => onOpenStudio(resume.resume_id)}
          className="flex-1 py-3 text-sm font-semibold text-[#002fa7] hover:bg-[#eff6ff] transition-colors flex items-center justify-center space-x-1"
          data-testid={`studio-${resume.resume_id}`}
        >
          <Sparkles size={14} />
          <span>Studio</span>
        </button>
        <button
          onClick={() => onDuplicate(resume.resume_id)}
          className="flex-1 py-3 text-sm text-[#4b5563] hover:bg-[#f4f5f7] transition-colors flex items-center justify-center space-x-1 border-l border-[#e5e7eb]"
          data-testid={`duplicate-${resume.resume_id}`}
        >
          <Copy size={14} />
          <span>Duplicate</span>
        </button>
        <button
          onClick={() => onDelete(resume.resume_id)}
          className="flex-1 py-3 text-sm text-[#e11d48] hover:bg-[#fef2f2] transition-colors flex items-center justify-center space-x-1 border-l border-[#e5e7eb]"
          data-testid={`delete-${resume.resume_id}`}
        >
          <Trash2 size={14} />
          <span>Delete</span>
        </button>
      </div>
    </motion.div>
  );
};

// Empty State Component
const EmptyState = ({ hasFilters }) => (
  <div className="text-center py-16" data-testid="empty-state">
    <FileText size={64} className="mx-auto text-[#d1d5db] mb-4" />
    <h3 className="font-satoshi text-xl font-bold text-[#0f1115] mb-2">
      {hasFilters ? 'No resumes found' : 'No resumes yet'}
    </h3>
    <p className="text-[#6b7280] mb-6">
      {hasFilters
        ? 'Try adjusting your filters'
        : 'Create your first resume to get started'}
    </p>
    {!hasFilters && (
      <Link to="/resume/new" className="btn-primary inline-flex items-center space-x-2">
        <Plus size={20} />
        <span>Create Resume</span>
      </Link>
    )}
  </div>
);

// Loading Skeleton Component
const LoadingSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[1, 2, 3].map((i) => (
      <div key={`skeleton-${i}`} className="dashboard-card p-6 animate-pulse">
        <div className="h-6 bg-[#e5e7eb] w-3/4 mb-4"></div>
        <div className="h-4 bg-[#e5e7eb] w-1/2 mb-6"></div>
        <div className="h-20 bg-[#e5e7eb]"></div>
      </div>
    ))}
  </div>
);

// Filters Bar Component
const ResumeFilters = ({ searchTerm, setSearchTerm, filterScore, setFilterScore, sortBy, setSortBy }) => (
  <div className="flex flex-col md:flex-row gap-4 mb-6">
    <div className="relative flex-1">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
      <input
        type="text"
        placeholder="Search resumes..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full pl-12 pr-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] focus:border-[#002fa7] transition-colors"
        data-testid="search-input"
      />
    </div>
    <div className="flex gap-4">
      <div className="relative">
        <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
        <select
          value={filterScore}
          onChange={(e) => setFilterScore(e.target.value)}
          className="pl-12 pr-8 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] appearance-none cursor-pointer"
          data-testid="filter-score"
        >
          <option value="all">All Scores</option>
          <option value="high">High (75+)</option>
          <option value="medium">Medium (50-75)</option>
          <option value="low">Low (&lt;50)</option>
        </select>
      </div>
      <select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        className="px-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] appearance-none cursor-pointer"
        data-testid="sort-by"
      >
        <option value="date">Sort by Date</option>
        <option value="score">Sort by Score</option>
      </select>
    </div>
  </div>
);

// Resume Grid Component
const ResumeGrid = ({ loading, filteredResumes, hasFilters, onReoptimize, onDelete, onDuplicate, onOpenStudio }) => {
  if (loading) return <LoadingSkeleton />;
  if (filteredResumes.length === 0) return <EmptyState hasFilters={hasFilters} />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredResumes.map((resume, index) => (
        <ResumeCard
          key={resume.resume_id}
          resume={resume}
          onReoptimize={onReoptimize}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          onOpenStudio={onOpenStudio}
          index={index}
        />
      ))}
    </div>
  );
};

// AI Tools Grid Component
const AIToolsGrid = () => (
  <div className="mb-8">
    <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6b7280] mb-3">AI Tools</h2>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Link to="/cover-letter" className="dashboard-card p-4 hover:border-[#002fa7] border border-transparent transition-colors group" data-testid="tool-cover-letter">
        <PenLine size={20} className="text-[#002fa7] mb-2 group-hover:scale-110 transition-transform" />
        <p className="text-sm font-bold text-[#0f1115]">Cover Letter</p>
        <p className="text-xs text-[#6b7280] mt-0.5">AI-generated letters</p>
      </Link>
      <Link to="/linkedin-optimizer" className="dashboard-card p-4 hover:border-[#0077b5] border border-transparent transition-colors group" data-testid="tool-linkedin">
        <Linkedin size={20} className="text-[#0077b5] mb-2 group-hover:scale-110 transition-transform" />
        <p className="text-sm font-bold text-[#0f1115]">LinkedIn</p>
        <p className="text-xs text-[#6b7280] mt-0.5">Optimize your profile</p>
      </Link>
      <Link to="/interview-prep" className="dashboard-card p-4 hover:border-[#10b981] border border-transparent transition-colors group" data-testid="tool-interview">
        <MessageSquare size={20} className="text-[#10b981] mb-2 group-hover:scale-110 transition-transform" />
        <p className="text-sm font-bold text-[#0f1115]">Interview Prep</p>
        <p className="text-xs text-[#6b7280] mt-0.5">Practice questions</p>
      </Link>
      <Link to="/referral" className="dashboard-card p-4 hover:border-[#eab308] border border-transparent transition-colors group" data-testid="tool-referral">
        <Gift size={20} className="text-[#eab308] mb-2 group-hover:scale-110 transition-transform" />
        <p className="text-sm font-bold text-[#0f1115]">Referrals</p>
        <p className="text-xs text-[#6b7280] mt-0.5">Earn bonus credits</p>
      </Link>
    </div>
  </div>
);

const Dashboard = () => {
  const { user, refreshAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScore, setFilterScore] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [subscription, setSubscription] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [studioResumeId, setStudioResumeId] = useState(null);
  const [tailorIntent, setTailorIntent] = useState(null); // { job, source_target_job }
  // Soft paywall modal — fires when a free-tier user at their resume
  // cap clicks "New Resume". We block navigation BEFORE the backend
  // 403 because the modal converts at the moment of strongest intent
  // (they just decided they want a 2nd resume) and keeps them on the
  // dashboard where their first resume sits as social proof.
  const [showPaywall, setShowPaywall] = useState(false);
  const [tailorIntentDismissed, setTailorIntentDismissed] = useState(false);
  const apiRef = useRef(API);

  const fetchResumes = useCallback(async () => {
    try {
      const response = await axios.get(`${apiRef.current}/resumes`, { withCredentials: true });
      setResumes(response.data);
    } catch (err) {
      logger.error('Failed to fetch resumes:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${apiRef.current}/payments/subscription`, { withCredentials: true });
      setSubscription(response.data);
    } catch (err) {
      logger.error('Failed to fetch subscription:', err.message);
    }
  }, []);

  const checkPayment = useCallback(async (sessionId) => {
    try {
      const response = await axios.get(`${apiRef.current}/payments/status/${sessionId}`, { withCredentials: true });
      setPaymentStatus(response.data);
      if (response.data.payment_status === 'paid') {
        // Fire GA4 purchase event exactly once per session_id. Idempotent via
        // sessionStorage so a Dashboard refresh doesn't double-count revenue.
        try {
          const storageKey = `ga4_purchase_fired_${sessionId}`;
          if (!sessionStorage.getItem(storageKey)) {
            const planName = response.data.plan || response.data.product_name || 'pro';
            const amount = (response.data.amount_total || response.data.amount || 0) / 100;
            analytics.completePayment(planName, amount);
            sessionStorage.setItem(storageKey, '1');
          }
        } catch (_) { /* storage blocked — still fire once per page-load */ }
        await refreshAuth();
        await fetchSubscription();
      }
    } catch (err) {
      logger.error('Failed to check payment:', err.message);
    }
  }, [refreshAuth, fetchSubscription]);

  useEffect(() => {
    refreshAuth();
    fetchResumes();
    fetchSubscription();

    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    if (sessionId && payment === 'success') {
      checkPayment(sessionId);
    }

    // Tailor-intent deep-link from Free ATS job-match emails:
    //   /dashboard?intent=tailor&job_id=<hash>
    // Resolve the job_id back to a full payload via the authenticated user's
    // email so we can prefill the resume-tailor flow with zero friction.
    const intent = searchParams.get('intent');
    const jobId = searchParams.get('job_id');
    if (intent === 'tailor' && jobId) {
      (async () => {
        try {
          const res = await axios.get(
            `${apiRef.current}/free-ats-check/tailor-job/${encodeURIComponent(jobId)}`,
            { withCredentials: true }
          );
          if (res.data?.ok && res.data.job) {
            setTailorIntent({ job: res.data.job, source_target_job: res.data.source_target_job });
          }
        } catch (e) {
          // Silent: stale link or wrong account — user just sees normal dashboard.
          logger.warn?.('Tailor intent resolve failed:', e.message);
        }
      })();
    }
  }, [fetchResumes, fetchSubscription, searchParams, checkPayment, refreshAuth]);

  const handleDelete = useCallback(async (resumeId) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) return;
    try {
      await axios.delete(`${apiRef.current}/resumes/${resumeId}`, { withCredentials: true });
      setResumes(prev => prev.filter((r) => r.resume_id !== resumeId));
    } catch (err) {
      logger.error('Failed to delete resume:', err.message);
    }
  }, []);

  const handleReoptimize = useCallback(async (resumeId) => {
    try {
      const response = await axios.post(
        `${apiRef.current}/resumes/${resumeId}/reoptimize`,
        {},
        { withCredentials: true }
      );
      setResumes(prev => prev.map((r) => (r.resume_id === resumeId ? response.data : r)));
    } catch (err) {
      logger.error('Failed to reoptimize:', err.message);
    }
  }, []);

  const handleDuplicate = useCallback(async (resumeId) => {
    try {
      const response = await axios.post(
        `${apiRef.current}/resumes/${resumeId}/duplicate`,
        {},
        { withCredentials: true }
      );
      setResumes(prev => [response.data, ...prev]);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to duplicate resume.';
      alert(msg);
      logger.error('Failed to duplicate:', err.message);
    }
  }, []);

  const hasFilters = searchTerm || filterScore !== 'all';

  const filteredResumes = resumes
    .filter((resume) => {
      const matchesSearch =
        resume.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resume.target_job.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterScore === 'all') return matchesSearch;
      if (filterScore === 'low') return matchesSearch && resume.ats_score < 50;
      if (filterScore === 'medium') return matchesSearch && resume.ats_score >= 50 && resume.ats_score <= 75;
      if (filterScore === 'high') return matchesSearch && resume.ats_score > 75;
      return matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'date') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'score') return b.ats_score - a.ats_score;
      return 0;
    });

  const isUnlimited = subscription?.limit === -1 || subscription?.limit === Infinity;
  const getUsagePercentage = () => {
    if (!subscription) return 0;
    if (isUnlimited) return 0;
    return (subscription.resumes_this_month / subscription.limit) * 100;
  };
  const usagePercentage = getUsagePercentage();

  const averageScore = resumes.length > 0
    ? Math.round(resumes.reduce((acc, r) => acc + r.ats_score, 0) / resumes.length)
    : 0;

  const limitDisplay = isUnlimited ? 'Unlimited' : (subscription?.limit || 1);
  const isAtLimit = subscription && !isUnlimited && subscription.resumes_this_month >= subscription.limit;
  // Soft upsell: show plans CTA to free users at ≥50% monthly usage, or to
  // any free user without an active subscription. Targets users who get
  // real value from the product before they hit a hard wall — which GA
  // tells us is currently almost never (1 pricing-page view this year).
  const isFreeTier = subscription && !isUnlimited && subscription.limit <= 3;
  const showSoftUpsell = isFreeTier && !isAtLimit && (usagePercentage >= 50 || resumes.length >= 1);

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20" data-testid="dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Payment Success Banner */}
        {paymentStatus?.payment_status === 'paid' && (
          <motion.div
            initial={bannerSlideIn}
            animate={bannerSlideInAnimate}
            className="mb-6 p-4 bg-[#10b981] text-white flex items-center justify-between"
            data-testid="payment-success-banner"
          >
            <span>Payment successful! Your subscription has been updated.</span>
            <button onClick={() => setPaymentStatus(null)} className="text-white hover:text-[#d1d5db]">
              ×
            </button>
          </motion.div>
        )}

        {/* Tailor-intent banner — user clicked a job-match card in the Free
            ATS email, signed up, and landed here with ?intent=tailor&job_id=. */}
        {tailorIntent && !tailorIntentDismissed && (
          <motion.div
            initial={bannerSlideIn}
            animate={bannerSlideInAnimate}
            className="mb-6 p-5 bg-[#002fa7] text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            data-testid="tailor-intent-banner"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={16} />
                <span className="text-xs uppercase tracking-wider font-bold opacity-80">
                  Welcome — let's tailor your resume
                </span>
              </div>
              <p className="font-satoshi text-lg font-bold leading-snug">
                {tailorIntent.job.title}
                {tailorIntent.job.company && (
                  <span className="font-normal opacity-90"> · {tailorIntent.job.company}</span>
                )}
              </p>
              {tailorIntent.job.matched?.length > 0 && (
                <p className="text-xs opacity-80 mt-1">
                  {tailorIntent.job.score}% match · matched on {tailorIntent.job.matched.slice(0, 3).join(', ')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => {
                  navigate('/resume/new', {
                    state: {
                      fromTailorIntent: true,
                      title: `Resume for ${tailorIntent.job.title}${tailorIntent.job.company ? ` at ${tailorIntent.job.company}` : ''}`,
                      target_job: tailorIntent.job.title,
                      target_company: tailorIntent.job.company || '',
                      job_description: tailorIntent.job.description || '',
                      job_url: tailorIntent.job.url || '',
                    },
                  });
                }}
                className="bg-white text-[#002fa7] hover:bg-[#f4f5f7] px-4 py-2 font-bold text-sm inline-flex items-center gap-2 transition-colors"
                data-testid="tailor-intent-start-btn"
              >
                Tailor for this job <ArrowRight size={14} />
              </button>
              <button
                onClick={() => setTailorIntentDismissed(true)}
                className="text-white/70 hover:text-white px-2"
                aria-label="Dismiss"
                data-testid="tailor-intent-dismiss"
              >
                ×
              </button>
            </div>
          </motion.div>
        )}

        {/* Email Verification Banner */}
        {user && user.email_verified === false && (
          <motion.div
            initial={bannerSlideIn}
            animate={bannerSlideInAnimate}
            className="mb-6 p-4 bg-[#f59e0b] text-white flex items-center justify-between"
            data-testid="verify-email-banner"
          >
            <div className="flex items-center space-x-2">
              <AlertCircle size={20} />
              <span>Please verify your email address. Check your inbox for a verification link.</span>
            </div>
            <button
              onClick={async () => {
                try {
                  await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
                  alert('Verification email sent! Check your inbox.');
                } catch (err) {
                  logger.error('Failed to resend verification:', err.message);
                }
              }}
              className="text-white underline hover:no-underline whitespace-nowrap ml-4 font-semibold"
              data-testid="resend-verification-btn"
            >
              Resend Email
            </button>
          </motion.div>
        )}

        {/* Comp-trial countdown — shown to users on a time-boxed Pro grant
            (e.g. "1 week of Pro on us" recovery flows). Reads pro_expires_at
            from the user record; renders nothing for paying users. */}
        <CompTrialBanner expiresAt={user?.pro_expires_at} />

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115]">
              Dashboard
            </h1>
            <p className="text-[#4b5563] mt-1 flex items-center gap-2 flex-wrap">
              <span>Welcome back, {user?.name}</span>
              {user?.founding_member && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-[#fde68a] to-[#fbbf24] text-[#0f1115] text-xs font-bold uppercase tracking-wider"
                  title="You joined during Ascend's Founding Member launch"
                  data-testid="founding-member-badge"
                >
                  <Sparkles size={11} />
                  Founding Member
                </span>
              )}
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-3">
            <Link
              to="/templates"
              className="btn-outline inline-flex items-center space-x-2"
              data-testid="templates-button"
            >
              <LayoutGrid size={18} />
              <span>Templates</span>
            </Link>
            <Link
              to="/resume/new"
              className="btn-primary inline-flex items-center space-x-2"
              data-testid="create-resume-button"
              onClick={(e) => {
                // Intercept for free-tier-at-limit. Modal converts better
                // than the in-page banner because it's the exact moment
                // of highest commitment intent.
                if (isAtLimit) {
                  e.preventDefault();
                  setShowPaywall(true);
                }
              }}
            >
              <Plus size={20} />
              <span>New Resume</span>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatsCard 
            label="Total Resumes" 
            value={resumes.length} 
            icon={FileText} 
            iconColor="text-[#002fa7]" 
          />
          <StatsCard 
            label="Avg. Score" 
            value={averageScore} 
            icon={TrendingUp} 
            iconColor="text-[#10b981]" 
          />
          <div className="dashboard-card p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6b7280]">
                Plan
              </span>
              <Target size={20} className="text-[#eab308]" />
            </div>
            <div className="text-xl font-satoshi font-bold text-[#0f1115] capitalize" data-testid="subscription-tier">
              {subscription?.tier || 'Free'}
            </div>
          </div>
          <div className="dashboard-card p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6b7280]">
                Monthly Usage
              </span>
              <Calendar size={20} className="text-[#002fa7]" />
            </div>
            <div className="text-xl font-satoshi font-bold text-[#0f1115]" data-testid="monthly-usage">
              {subscription?.resumes_this_month || 0} / {limitDisplay}
            </div>
            <div className="mt-2 h-1 bg-[#e5e7eb]">
              <div
                className="h-full bg-[#002fa7]"
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>

        <FeatureUsageMeter />
        <AIToolsGrid />

        {/* Manage Subscription banner (active paid sub or cancel-scheduled) */}
        <ManageSubscriptionBanner
          subscription={subscription}
          onChange={fetchSubscription}
        />

        {/* Soft upsell banner — proactive (shown before the hard limit
            triggers) to drive Pricing-page views. GA showed 934 free-tool
            views vs 1 pricing-page view this year, so we need a soft nudge
            inside the post-signup experience. Dismissible per session by
            simply not showing once user is Pro or unlimited. */}
        {showSoftUpsell && (
          <div
            className="mb-6 p-5 bg-gradient-to-r from-[#002fa7] to-[#001d6a] text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            data-testid="soft-upsell-banner"
          >
            <div className="flex-1 min-w-0">
              <p className="font-satoshi font-bold text-base mb-1">
                You're getting real value — get the full toolkit on Pro
              </p>
              <p className="text-sm text-white/85">
                Unlimited resumes · AI rewrites · Cover letters · Interview prep · Sage coaching · All themes
              </p>
            </div>
            <Link
              to="/pricing?ref=dashboard_soft_upsell&utm_source=dashboard&utm_medium=in_app&utm_campaign=soft_upsell"
              className="shrink-0 bg-white text-[#002fa7] hover:bg-[#f4f5f7] px-5 py-2.5 font-bold text-sm inline-flex items-center gap-2 transition-colors"
              data-testid="soft-upsell-btn"
            >
              See plans <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {/* Limit Warning */}
        {isAtLimit && (
          <div className="mb-6 p-4 bg-[#fef3c7] border border-[#eab308] flex items-center space-x-4" data-testid="limit-warning">
            <AlertCircle size={24} className="text-[#eab308]" />
            <div>
              <p className="font-semibold text-[#92400e]">Monthly limit reached</p>
              <p className="text-sm text-[#a16207]">
                Upgrade your plan to create more resumes.{' '}
                <Link to="/pricing" className="underline">View plans</Link>
              </p>
            </div>
          </div>
        )}

        <ResumeFilters
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          filterScore={filterScore} setFilterScore={setFilterScore}
          sortBy={sortBy} setSortBy={setSortBy}
        />

        <ResumeGrid
          loading={loading} filteredResumes={filteredResumes}
          hasFilters={hasFilters} onReoptimize={handleReoptimize}
          onDelete={handleDelete} onDuplicate={handleDuplicate}
          onOpenStudio={setStudioResumeId}
        />
      </div>

      {studioResumeId && (
        <ResumeStudio
          resumeId={studioResumeId}
          onClose={() => {
            setStudioResumeId(null);
            fetchResumes();
          }}
        />
      )}

      {/* Soft paywall — fires when a free-tier user at the resume limit
          tries to create another resume. See ResumePaywallModal.js for
          conversion-strategy rationale. */}
      <ResumePaywallModal open={showPaywall} onClose={() => setShowPaywall(false)} />
    </div>
  );
};

export default Dashboard;
